import React, { useState, useContext, useMemo, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { gql, useQuery, useApolloClient } from '@apollo/client';
import { useAtomValue } from 'jotai';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import { DATE_FORMAT_DASH, TIME_FORMAT_UTC_OFFSET_TIMEZONE } from '../../utils/dates';
import { ExportIcon, Refresh } from '../../atoms/Icons';
import WebAppContext from '../../utils/webAppContext';
import { algorithmVersionAtom } from '../../utils/jotaiAtoms';
import { useAnalysisFilter } from '../../utils/useFeedFrameFilter';
import DocumentFactory from '../../utils/DocumentFactory/factory';
import CSVFeedEventStrategy from '../../utils/DocumentFactory/csvFeedEventStrategy';
import XlsxFeedEventStrategy from '../../utils/DocumentFactory/xlsxFeedEventStrategy';
import CSVFaultCodeStrategy from '../../utils/DocumentFactory/csvFaultCodeStrategy';
import XlsxFaultCodeStrategy from '../../utils/DocumentFactory/xlsxFaultCodeStrategy';
import CSVDailyConsumptionStrategy from '../../utils/DocumentFactory/csvDailyConsumptionStrategy';
import XLSXDailyConsumptionStrategy from '../../utils/DocumentFactory/xlsxDailyConsumptionStrategy';
import FeedFloProgress from '../../atoms/FeedFloProgress';
import { ComboboxMultiField } from '../../atoms/ComboboxMulti';
import { ComboboxField } from '../../atoms/Combobox';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { SelectField } from '../../atoms/Select';
import Checkbox from '../../atoms/Checkbox';
import Button from '../../atoms/Button';
import FeedFloDateRangeField from '../../organisms/FeedFloDateRangeInput/FeedFloDateRangeField';
import useUser from '../../utils/hooks/useUser';
import useFeature from '../../utils/hooks/useFeature';
import { Privilege, useCan } from '@feedflo/access-control';

const FARM_GQL = gql`
  query FarmDataQuery {
    farm(order_by: { name: asc }, where: { deleted_at: { _is_null: true } }) {
      id
      name
    }
  }
`;

const FORMAT_LIST = [
  { id: 'xlsx', name: 'Excel .xlsx' },
  { id: 'csv', name: 'Comma Separated Values .csv' },
];

const DATA_TYPE_LIST = [
  { id: 'feedFrame', name: 'Feed Events' },
  { id: 'faultCode', name: 'Alerts' },
  { id: 'dailyConsumption', name: 'Daily Consumption' },
];

const UNIT_LIST = [
  { id: 'metric', name: 'Metric (kg)' },
  { id: 'imperial', name: 'Imperial (lbs)' },
];

// Dynamic validation schema based on data type
const getValidationSchema = (dataType) => {
  const baseSchema = {
    dateRange: Yup.object().shape({
      from: Yup.date().required('Start date is required'),
      to: Yup.date().required('End date is required').min(Yup.ref('from'), 'End date cannot be before start date'),
    }),
    dataType: Yup.string().required('Data type is required'),
    dataFormat: Yup.string().required('Data format is required'),
  };

  if (dataType === 'dailyConsumption') {
    return Yup.object().shape({
      ...baseSchema,
      barnId: Yup.string().required('A barn must be selected'),
      dataUnit: Yup.string().required('Data unit is required'),
    });
  } else {
    return Yup.object().shape({
      ...baseSchema,
      barnIds: Yup.array().of(Yup.string()).min(1, 'At least one barn must be selected'),
      ...(dataType === 'feedFrame' && {
        dataUnit: Yup.string().required('Data unit is required'),
      }),
    });
  }
};

export default function ExportsController() {
  const gqlClient = useApolloClient();
  const [progress, setProgress] = useState(0);
  const [documentFactoryState, setDocumentFactoryState] = useState('ready');
  const [includeStaffOnlyRootCauses, setIncludeStaffOnlyRootCauses] = useState(false);

  const algorithmVersion = useAtomValue(algorithmVersionAtom);
  const { isMetric } = useContext(WebAppContext);
  const timeZoneString = dayjs.tz().format(TIME_FORMAT_UTC_OFFSET_TIMEZONE);

  const analysisFilter = useAnalysisFilter();
  const { data, loading, refetch } = useQuery(FARM_GQL);
  const farms = data?.farm;

  const { user } = useUser();
  const { active: showStaffUI } = useFeature('SHOW_STAFF_ONLY_ELEMENTS');
  const { can } = useCan();

  useEffect(() => {
    // Trigger refresh when algorithm version changes or is reset
    refetch();
  }, [algorithmVersion, refetch]);

  // Track previous data type to reset barn selection when it changes
  const [previousDataType, setPreviousDataType] = useState('');

  function startExport(to, from, barnIds, barnId, fileFormat, dataType, massUnitSystem, includeStaffOnlyRootCauses) {
    setDocumentFactoryState('downloading');
    let strategy = null;
    
    // For daily consumption, use single barnId; for others, use barnIds array
    const finalBarnIds = dataType === 'dailyConsumption' ? [barnId] : barnIds;
    
    switch (`${fileFormat}|${dataType}`) {
      case 'csv|feedFrame':
        strategy = new CSVFeedEventStrategy(massUnitSystem === 'metric');
        break;
      case 'xlsx|feedFrame':
        strategy = new XlsxFeedEventStrategy(massUnitSystem === 'metric');
        break;
      case 'csv|faultCode':
        strategy = new CSVFaultCodeStrategy();
        break;
      case 'xlsx|faultCode':
        strategy = new XlsxFaultCodeStrategy();
        break;
      case 'csv|dailyConsumption':
        strategy = new CSVDailyConsumptionStrategy(massUnitSystem === 'metric');
        break;
      case 'xlsx|dailyConsumption':
        strategy = new XLSXDailyConsumptionStrategy(massUnitSystem === 'metric');
        break;

      default:
        break;
    }

    const factory = new DocumentFactory({
      farmIds: finalBarnIds,
      startTime: from.toDate(),
      stopTime: to.toDate(),
      strategy,
      progressCallback: (x) => {
        setProgress(x);
        setDocumentFactoryState('downloading');
      },
      gqlClient,
      analysisFilter: dataType === 'feedFrame' ? analysisFilter : null,
      includeStaffOnlyRootCauses: dataType === 'faultCode' ? includeStaffOnlyRootCauses : false,
      algorithmVersion,
      timeZone: user?.timezone,
    });

    factory
      .start()
      .then(() => {
        const datePart = `${from.format(DATE_FORMAT_DASH)}_${to.format(DATE_FORMAT_DASH)}`;
        const barnPart = dataType === 'dailyConsumption' 
          ? barnListItems.find(b => b.id === barnId)?.name || 'barn'
          : `${finalBarnIds.length} barns`;
        const dataTypeString = dataType;
        try {
          saveAs(factory.createFile(), `${barnPart}_${dataTypeString}_${datePart}.${fileFormat}`);
        } catch (e) {
          Sentry?.captureException(e);
        }
      })
      .finally(() => {
        setDocumentFactoryState('done');
      });
  }

  const barnListItems = useMemo(() => {
    return farms
      ?.filter((f) => can('barn', f.id, Privilege.BARN_EXPORT))
      .map((f) => {
        return {
          id: f.id,
          name: f.name,
        };
      });
  }, [farms, can]);

  // Only wait for analysisFilter if feedFrame export is available
  const needsAnalysisFilter = true; // Keep for now, but could be conditional
  if (needsAnalysisFilter && !analysisFilter) return <div>Loading...</div>;
  
  return (
    <div className="ExportController">
      <Formik
        onSubmit={(values) => {
          startExport(
            values.dateRange.to,
            values.dateRange.from,
            values.barnIds,
            values.barnId,
            values.dataFormat,
            values.dataType,
            values.dataUnit,
            includeStaffOnlyRootCauses,
          );
        }}
        initialValues={{
          dateRange: { from: dayjs().subtract(6, 'months'), to: dayjs() },
          barnIds: [],
          barnId: '',
          dataType: '',
          dataUnit: isMetric ? 'metric' : 'imperial',
          dataFormat: 'xlsx',
        }}
        validationSchema={Yup.lazy((values) => getValidationSchema(values.dataType))}
        enableReinitialize={false}
      >
        {({ handleSubmit, values, setFieldValue }) => {
          const selectedDataType = values.dataType;
          const isDailyConsumption = selectedDataType === 'dailyConsumption';
          const isFeedFrame = selectedDataType === 'feedFrame';
          const isFaultCode = selectedDataType === 'faultCode';
          const needsUnits = isFeedFrame || isDailyConsumption;

          // Reset barn selection when data type changes
          useEffect(() => {
            if (selectedDataType && selectedDataType !== previousDataType) {
              setPreviousDataType(selectedDataType);
              if (isDailyConsumption) {
                setFieldValue('barnIds', []);
                setFieldValue('barnId', '');
              } else {
                setFieldValue('barnId', '');
                setFieldValue('barnIds', []);
              }
            }
          }, [selectedDataType, setFieldValue, isDailyConsumption, previousDataType]);

          return (
            <Form>
              <div className="ExportController-Form">
                {/* Step 1: Select Export Type */}
                <SelectField
                  name="dataType"
                  defaultText="Select a Data Type"
                  label="Type of Data"
                  itemList={DATA_TYPE_LIST}
                  disabled={documentFactoryState !== 'ready'}
                />

                {/* Step 2: Show fields based on selected type */}
                {selectedDataType && (
                  <>
                    <FeedFloDateRangeField
                      label={`Export Date Range ${timeZoneString}`}
                      name="dateRange"
                      max={dayjs()}
                      disabled={documentFactoryState !== 'ready'}
                    />

                    {/* Barn Selection - Single for Daily Consumption, Multiple for others */}
                    {isDailyConsumption ? (
                      <ComboboxField
                        name="barnId"
                        items={barnListItems}
                        loading={loading}
                        label="Select Barn"
                        emptyPlaceholder="Select a Barn"
                        disabled={documentFactoryState !== 'ready'}
                      />
                    ) : (
                      <ComboboxMultiField
                        name="barnIds"
                        items={barnListItems}
                        loading={loading}
                        label="Export Barns"
                        emptyPlaceholder="Select Barns"
                        itemNameSingular="Barn"
                        itemNamePlural="Barns"
                        allowSelectAll={true}
                        disabled={documentFactoryState !== 'ready'}
                      />
                    )}

                    {/* Units - Only for Feed Events and Daily Consumption */}
                    {needsUnits && (
                      <SelectField
                        name="dataUnit"
                        defaultText="Select Weight Units"
                        label="Units"
                        disabled={documentFactoryState !== 'ready'}
                        itemList={UNIT_LIST}
                      />
                    )}

                    {/* File Format */}
                    <SelectField
                      name="dataFormat"
                      defaultText="Select Data Format"
                      label="File Format"
                      itemList={FORMAT_LIST}
                      disabled={documentFactoryState !== 'ready'}
                    />

                    {/* Staff-only checkbox - Only for Alerts */}
                    {isFaultCode && user?.isStaff && showStaffUI && (
                      <Checkbox
                        label="Include Faults with FeedFlo Staff-Only Root Causes"
                        checked={includeStaffOnlyRootCauses}
                        onChange={(e) => setIncludeStaffOnlyRootCauses(e.target.checked)}
                        disabled={documentFactoryState !== 'ready'}
                      />
                    )}

                    {documentFactoryState === 'ready' && (
                      <Button
                        loading={loading}
                        disabled={documentFactoryState !== 'ready'}
                        variant="vivid"
                        color="success"
                        content={'Export'}
                        left={<ExportIcon className="white" />}
                        onClick={handleSubmit}
                      />
                    )}
                    {documentFactoryState === 'downloading' && <FeedFloProgress percent={progress} />}
                    {documentFactoryState === 'done' && (
                      <Button
                        loading={loading}
                        variant="pastel"
                        color="success"
                        content={'Reset'}
                        left={<Refresh className="white" />}
                        onClick={() => setDocumentFactoryState('ready')}
                      />
                    )}
                  </>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}
